import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

const AuthContext = createContext({
  user: null,
  loading: true,
  isLocalMode: false,
  refetchUser: async () => {},
  enterLocalMode: () => {},
  exitLocalMode: () => {},
  logout: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLocalMode, setIsLocalMode] = useState(() => localStorage.getItem('local_mode') === 'true');

  const checkAuth = useCallback(async () => {
    // Local mode — skip auth
    if (localStorage.getItem('local_mode') === 'true') {
      setIsLocalMode(true);
      setLoading(false);
      return;
    }

    // Returning from OAuth callback — AuthCallback exchanges the session_id
    // for an httpOnly cookie and then calls refetchUser() explicitly.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Legacy cleanup: the session token used to live in localStorage. It's
    // now an httpOnly cookie, so scrub any stale value that predates the
    // migration (it's no longer used for auth).
    if (localStorage.getItem('session_token')) {
      localStorage.removeItem('session_token');
    }

    // `auth_hint` is a NON-sensitive flag (never the token) that lets us
    // skip the /me round-trip for visitors who have never logged in.
    const authHint = localStorage.getItem('auth_hint');
    if (!authHint) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // The httpOnly session cookie is sent automatically (same-origin).
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('auth_hint');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refetchUser = checkAuth;

  const enterLocalMode = useCallback(() => {
    localStorage.setItem('local_mode', 'true');
    setIsLocalMode(true);
  }, []);

  const exitLocalMode = useCallback(() => {
    localStorage.removeItem('local_mode');
    setIsLocalMode(false);
  }, []);

  const logout = useCallback(async () => {
    // Clear the server session + httpOnly cookie (best-effort; the CSRF
    // header is attached by the axios interceptor).
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`);
    } catch (e) {
      console.debug('logout request failed (non-fatal):', e?.message || e);
    }
    localStorage.removeItem('auth_hint');
    localStorage.removeItem('session_token'); // legacy cleanup
    localStorage.removeItem('local_mode');
    setUser(null);
    setIsLocalMode(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, isLocalMode, refetchUser, enterLocalMode, exitLocalMode, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
